import React, {
  useRef,
  useCallback,
  ReactElement,
  MouseEvent,
  useEffect
} from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { mq, mqNumberSizes, FocusTrap } from '@kvdbil/components';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/Locale';
import { useWindowSize } from '~/App/shared/hooks/useWindowSize';
import { closeBiddingView } from '~/App/shared/actions/objectViewDrawerActions';
import { useScrollLock } from '~/App/shared/hooks/useScrollLock';

const TransitionContainer = styled.div`
  .slideTransition-enter {
    transform: translate3d(10vw, 0, 0);
  }

  .slideTransition-enter.slideTransition-enter-active {
    transform: translate3d(0, 0, 0);
    transition:
      transform 0.2s cubic-bezier(0.1, 1, 0.22, 1),
      opacity 0.2s;
  }

  .slideTransition-exit {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }

  .slideTransition-exit.slideTransition-exit-active {
    transition:
      transform 0.2s cubic-bezier(0.215, 0.61, 0.355, 1),
      opacity 0.3s;
    opacity: 0;
    transform: translate3d(20vw, 0, 0);

    ${mq('tablet')} {
      transform: translate3d(10vw, 0, 0);
    }
  }
`;

const Background = styled.div`
  position: fixed;
  z-index: 800000;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.4);
`;

const Container = styled.div`
  position: fixed;
  z-index: 800000;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0px;
  overscroll-behavior: contain;
`;

const Content = styled.aside<{ maxWidth?: number }>`
  margin-left: auto;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background.light};

  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}px;`}

  ${mq('laptop')} {
    ${({ maxWidth }) => !maxWidth && 'width: calc(50% + 400px);'}
  }
`;

const ChildrenWrapper = styled.div`
  height: 100%;
  position: relative;
  overflow-y: scroll;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

const transitionProps = {
  classNames: 'slideTransition',
  timeout: {
    enter: 600,
    exit: 600
  }
};

const backgroundTransitionprops = {
  classNames: 'backgroundTransition',
  timeout: {
    enter: 400,
    exit: 400
  }
};

interface Props {
  isOpen: boolean;
  children: ReactElement;
  contentMaxWidth?: number;
}

const BidOrReserveDrawer = ({ children, isOpen, contentMaxWidth }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  useScrollLock(isOpen);

  const showBackground =
    typeof width !== 'undefined' &&
    width > (contentMaxWidth ?? mqNumberSizes.laptop);

  const onClose = useCallback(() => {
    dispatch(closeBiddingView());
  }, [dispatch]);

  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const isOutside = event.target == contentRef.current;
      isOutside && onClose();
    },
    [onClose]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    isOpen && window.addEventListener('keydown', handleKeyDown);

    return () => {
      isOpen && window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <TransitionContainer>
      {showBackground && (
        <CSSTransition
          in={isOpen}
          {...backgroundTransitionprops}
          nodeRef={backgroundRef}
          unmountOnExit
        >
          <Background ref={backgroundRef} />
        </CSSTransition>
      )}

      <CSSTransition
        in={isOpen}
        {...transitionProps}
        nodeRef={contentRef}
        unmountOnExit
      >
        <Container ref={contentRef} onMouseDown={handleMouseDown}>
          <Content
            maxWidth={contentMaxWidth}
            role="dialog"
            aria-modal="true"
            aria-label={t('Object bid or reservation panel')}
            id="object-bid-reserve-drawer"
          >
            <ChildrenWrapper>
              <FocusTrap pause={!isOpen}>{children}</FocusTrap>
            </ChildrenWrapper>
          </Content>
        </Container>
      </CSSTransition>
    </TransitionContainer>
  );
};

export default BidOrReserveDrawer;
